import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, RouterStateSnapshot } from '@angular/router';
import { Router } from '@angular/router';

import { LanguageService } from '@rpgc-ai/services/translate/language.service';
import type { SupportedLanguageCode } from '@rpgc-ai/services/translate/languages';
import { DEFAULT_LANGUAGE } from '@rpgc-ai/services/translate/languages';

import { TranslocoService } from '@jsverse/transloco';

@Injectable({ providedIn: 'root' })
export class LanguageGuard implements CanActivate {
  // Injections
  readonly #router = inject(Router);

  readonly #languageService = inject(LanguageService);
  readonly #translocoService = inject(TranslocoService);

  canActivate(_route: ActivatedRouteSnapshot, { url }: RouterStateSnapshot): MaybeAsync<GuardResult> {
    const ignoredLanguageRedirect = this.#languageService.getIgnoredLanguageHistoryRedirect(url);
    if (ignoredLanguageRedirect) {
      return ignoredLanguageRedirect;
    }

    const savedLang = this.#languageService.initLanguage();
    const langFromPath = url.match(/^\/([a-z]{2})/)?.[1] as SupportedLanguageCode;

    if (savedLang) {
      if (this.#translocoService.getActiveLang() !== savedLang) {
        this.#translocoService.setActiveLang(savedLang);
      }
      this.#languageService.setNewLanguageToCookies(savedLang);
      return this.#languageService.isLangValid(langFromPath) ? true : this.#router.parseUrl(`/${savedLang}${url}`);
    }

    const newUrl = `/${DEFAULT_LANGUAGE}${url}`;

    return this.#router.parseUrl(newUrl);
  }
}
